import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Suppress noisy React 18.3 deprecation warnings from abandoned semantic-ui-react v2.x
if (process.env.NODE_ENV !== "production") {
	const originalError = console.error;
	console.error = (...args) => {
		if (typeof args[0] === "string" && args[0].includes("Support for defaultProps will be removed")) return;
		if (typeof args[0] === "string" && args[0].includes("findDOMNode is deprecated")) return;
		originalError(...args);
	};
}

import { createBrowserHistory } from "history";
import React from "react";
import { CookiesProvider } from "react-cookie";
import { createRoot } from "react-dom/client";
import { Provider as ReduxProvider } from "react-redux"; //-- This wil provide Redux store data to our React components.
import { Route, Router } from "react-router-dom";
import { Auth0Provider } from "./auth/auth0";
import App from "./components/App";
import configureStore from "./redux/store/configureStore";

import { Icon } from "semantic-ui-react";
import "react-widgets/styles.css"; //-- This is the css that is used for the DateTimePicker

// Extend Icon's name validator to accept our custom icon font names (icons.css / Core Line font).
// semantic-ui-react v2.x only knows its own bundled icon names and warns on anything else.
if (process.env.NODE_ENV !== "production" && Icon.propTypes) {
	// Names are space-separated as passed to the `name`/`icon` prop, matching actual usage in source.
	const customIconNames = new Set([
		"align vertical",
		"book out",
		"cartesian",
		"checkmark-square-outline",
		"data layers",
		"download solid",
		"drone",
		"farm",
		"folder open solid",
		"frame",
		"image edit",
		"license",
		"line graph decrease",
		"line graph increase",
		"mfa",
		"next",
		"parcels",
		"previous",
		"sun haze",
		"table body",
		"user group",
		"user settings",
	]);
	const originalNameValidator = Icon.propTypes.name;
	Icon.propTypes = {
		...Icon.propTypes,
		name: (props, propName, componentName) => {
			if (customIconNames.has(props[propName])) return null;
			return originalNameValidator(props, propName, componentName);
		},
	};
}

export const reduxStore = configureStore();
const browserHistory = createBrowserHistory({
	basename: process.env.PUBLIC_PATH,
});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
		},
	},
});

const container = document.getElementById("app");
const root = createRoot(container);
root.render(
	<Auth0Provider
		domain={process.env.aerialPlotConnections.aerialPlotWeb.domain}
		clientId={process.env.aerialPlotConnections.aerialPlotWeb.clientId}
		useRefreshTokens={true}
		cacheLocation="localstorage"
		authorizationParams={{
			redirect_uri: process.env.aerialPlotConnections.aerialPlotWeb.redirectUri,
			audience: process.env.aerialPlotConnections.aerialPlotWeb.apiAudience,
		}}
		onRedirectCallback={(appState) => browserHistory.replace(appState?.returnTo || "/")}
	>
		<ReduxProvider store={reduxStore}>
			<QueryClientProvider client={queryClient}>
				<Router history={browserHistory}>
					<Route>
						<CookiesProvider>
							<App browserHistory={browserHistory} />
						</CookiesProvider>
					</Route>
				</Router>
			</QueryClientProvider>
		</ReduxProvider>
	</Auth0Provider>,
);
